/**
 * @file planSummary.ts
 * @brief What plan this account is on, in one shape for every surface: the
 *        plan pill (user menu, Home), the "Your plan" card (company admin and
 *        personal Billing) and the plan-change dialog. Web and the Mac app.
 *
 * @details
 * Source: GET /billing/subscriptions. Plans-visible brief (21-10-2026):
 * - A company admin reads `companyPlan` (BACKEND Part 1). Until that ships,
 *   the same fields are worked out from today's `teamPlan` + the catalogue.
 * - A personal user reads `subscription` + the catalogue (and the server's
 *   `nextBillDate` / `nextBillAmount` when it sends them).
 * - A company member gets the company's plan name only: no money fields.
 * Pay as you go and the Free tier both show as "Free": the pill only ever
 * says Free, Pro or Business (Enterprise for an Enterprise company).
 * No VAT field (W36, 01-11-2026): VERBLIZR is not VAT registered, so the
 * card no longer says "VAT included" (Business was "plus VAT" under ADR-045).
 *
 * @author DES
 * @date 21-10-2026
 */

export type PlanAudience = "personal" | "company_admin" | "company_member";
export type PlanCode = "free" | "pro" | "business" | "enterprise";

export type PlanSummary = {
  audience: PlanAudience;
  planCode: PlanCode;
  planName: string;
  /** Money and dates: null for a member, or where there is no paid plan. */
  interval: "month" | "year" | null;
  status: string | null;
  live: boolean;
  pastDue: boolean;
  cancelAtPeriodEnd: boolean;
  /** Business: seats (= active members) and the minimum. */
  seats: number | null;
  minSeats: number | null;
  /** Pro: the plan price. Business: the price per seat. Minor units. */
  priceMinor: number | null;
  currency: string;
  /** End of the paid period (when a cancelling plan ends). */
  periodEnd: string | null;
  nextBillDate: string | null;
  nextBillAmountMinor: number | null;
  /** True when the amount is the server's (Stripe's upcoming invoice), not worked out here. */
  nextBillExact: boolean;
  /** The plan's charge before credit: price × seats (or the plan price). */
  planChargeMinor: number | null;
  /** Credit Stripe applies to the next bill, only when the server sends it. */
  nextBillCreditMinor: number | null;
  intelUsed: number | null;
  intelIncluded: number | null;
};

type Num = number | null | undefined;
type CatalogRow = {
  code: string;
  name: string;
  currency?: string | null;
  priceMinor?: Num;
  annualPriceMinor?: Num;
  minSeats?: Num;
  includedMinutes?: Num;
  enterprise?: boolean | null;
};
type Usage = { usedMinutes?: Num; includedMinutes?: Num; pools?: { intelligence?: { used?: Num; included?: Num } | null } | null } | null;
type Sub = {
  planCode: string;
  status: string;
  currentPeriodEnd?: string | null;
  billingInterval?: string | null;
  cancelAtPeriodEnd?: boolean | null;
  nextBillDate?: string | null;
  nextBillAmount?: Num;
  nextBillCredit?: Num;
} | null;
type TeamPlan = {
  planCode?: string | null;
  status?: string | null;
  quantity?: Num;
  seatCount?: Num;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean | null;
  currency?: string | null;
  interval?: string | null;
} | null;
/** BACKEND Part 1's read for a company (21-10-2026 contract). */
type CompanyPlan = {
  planCode?: string | null;
  interval?: string | null;
  status?: string | null;
  seats?: Num;
  minSeats?: Num;
  pricePerSeat?: Num;
  currency?: string | null;
  taxBehavior?: string | null;
  nextBillDate?: string | null;
  nextBillAmount?: Num;
  nextBillCredit?: Num;
  cancelAtPeriodEnd?: boolean | null;
  includedIntelMinutes?: Num;
  intelMinutesUsed?: Num;
  periodEnd?: string | null;
} | null;

export type PlanStatusInput = {
  currency?: string | null;
  catalog?: CatalogRow[] | null;
  subscription?: Sub;
  usage?: Usage;
  teamPlan?: TeamPlan;
  companyPlan?: CompanyPlan;
  enterprise?: unknown;
};

const LIVE = new Set(["active", "trialing", "past_due"]);
const NAMES: Record<PlanCode, string> = { free: "Free", pro: "Pro", business: "Business", enterprise: "Enterprise" };

function num(v: Num): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function code(raw: string | null | undefined): PlanCode {
  const c = String(raw ?? "").toLowerCase();
  return c === "pro" || c === "business" || c === "enterprise" ? c : "free";
}

function intervalOf(raw: string | null | undefined): "month" | "year" {
  return String(raw ?? "").toLowerCase().startsWith("year") ? "year" : "month";
}

function nameFor(c: PlanCode, catalog: CatalogRow[]): string {
  return c === "free" ? NAMES.free : (catalog.find((p) => p.code === c)?.name ?? NAMES[c]);
}

function empty(audience: PlanAudience, planCode: PlanCode, planName: string, currency: string): PlanSummary {
  return {
    audience,
    planCode,
    planName,
    interval: null,
    status: null,
    live: false,
    pastDue: false,
    cancelAtPeriodEnd: false,
    seats: null,
    minSeats: null,
    priceMinor: null,
    currency,
    periodEnd: null,
    nextBillDate: null,
    nextBillAmountMinor: null,
    nextBillExact: false,
    planChargeMinor: null,
    nextBillCreditMinor: null,
    intelUsed: null,
    intelIncluded: null,
  };
}

/**
 * The plan for this account.
 * @param memberCount Active company members (Business seats) when the server
 *   does not send seats yet; ignored otherwise.
 */
export function summarizePlan(
  data: PlanStatusInput | null | undefined,
  audience: PlanAudience,
  { memberCount = null }: { memberCount?: number | null } = {},
): PlanSummary {
  const catalog = data?.catalog ?? [];
  const currency = (data?.currency ?? "GBP").toUpperCase();

  if (audience !== "personal") {
    const cp = data?.companyPlan ?? null;
    const tp = data?.teamPlan ?? null;
    const enterprise = !!data?.enterprise;
    const planCode: PlanCode = enterprise
      ? "enterprise"
      : cp?.planCode
        ? code(cp.planCode)
        : tp && LIVE.has(String(tp.status ?? "")) ? code(tp.planCode) : "free";
    const planName = nameFor(planCode, catalog);
    // A member sees the plan's name and nothing else (brief, Part 1.2).
    if (audience === "company_member" || planCode === "free" || planCode === "enterprise") {
      return empty(audience, planCode, planName, currency);
    }
    const row = catalog.find((p) => p.code === planCode);
    const status = cp?.status ?? tp?.status ?? null;
    const minSeats = num(cp?.minSeats) ?? num(row?.minSeats) ?? 2;
    const seats = num(cp?.seats) ?? num(tp?.quantity) ?? num(tp?.seatCount) ?? (memberCount != null ? Math.max(minSeats, memberCount) : null);
    const interval = intervalOf(cp?.interval ?? tp?.interval);
    const priceMinor = num(cp?.pricePerSeat) ?? num(interval === "year" ? row?.annualPriceMinor : row?.priceMinor);
    const cancelAtPeriodEnd = !!(cp?.cancelAtPeriodEnd ?? tp?.cancelAtPeriodEnd);
    const serverAmount = num(cp?.nextBillAmount);
    const included = num(cp?.includedIntelMinutes) ?? (num(row?.includedMinutes) != null && seats != null ? num(row?.includedMinutes)! * seats : null);
    return {
      audience,
      planCode,
      planName,
      interval,
      status,
      live: LIVE.has(String(status ?? "")),
      pastDue: status === "past_due",
      cancelAtPeriodEnd,
      seats,
      minSeats,
      priceMinor,
      currency: (cp?.currency ?? tp?.currency ?? row?.currency ?? currency).toUpperCase(),
      periodEnd: cp?.periodEnd ?? cp?.nextBillDate ?? tp?.currentPeriodEnd ?? null,
      nextBillDate: cancelAtPeriodEnd ? null : (cp?.nextBillDate ?? tp?.currentPeriodEnd ?? null),
      nextBillAmountMinor: cancelAtPeriodEnd
        ? null
        : (serverAmount ?? (priceMinor != null && seats != null ? priceMinor * seats : null)),
      nextBillExact: serverAmount != null,
      planChargeMinor: priceMinor != null && seats != null ? priceMinor * seats : null,
      nextBillCreditMinor: cancelAtPeriodEnd ? null : num(cp?.nextBillCredit),
      intelUsed: num(cp?.intelMinutesUsed),
      intelIncluded: included,
    };
  }

  const sub = data?.subscription ?? null;
  if (!sub || !LIVE.has(sub.status)) return empty(audience, "free", NAMES.free, currency);
  const planCode = code(sub.planCode);
  const row = catalog.find((p) => p.code === sub.planCode);
  const interval = intervalOf(sub.billingInterval);
  const priceMinor = num(interval === "year" ? row?.annualPriceMinor : row?.priceMinor);
  const cancelAtPeriodEnd = !!sub.cancelAtPeriodEnd;
  const serverAmount = num(sub.nextBillAmount);
  const usage = data?.usage ?? null;
  return {
    audience,
    planCode,
    planName: row?.name ?? NAMES[planCode],
    interval,
    status: sub.status,
    live: true,
    pastDue: sub.status === "past_due",
    cancelAtPeriodEnd,
    seats: null,
    minSeats: null,
    priceMinor,
    currency: (row?.currency ?? currency).toUpperCase(),
    periodEnd: sub.currentPeriodEnd ?? null,
    nextBillDate: cancelAtPeriodEnd ? null : (sub.nextBillDate ?? sub.currentPeriodEnd ?? null),
    nextBillAmountMinor: cancelAtPeriodEnd ? null : (serverAmount ?? priceMinor),
    nextBillExact: serverAmount != null,
    planChargeMinor: priceMinor,
    nextBillCreditMinor: cancelAtPeriodEnd ? null : num(sub.nextBillCredit),
    intelUsed: num(usage?.pools?.intelligence?.used) ?? num(usage?.usedMinutes),
    intelIncluded: num(usage?.pools?.intelligence?.included) ?? num(usage?.includedMinutes),
  };
}

/** Yearly saving against twelve months, in whole percent; null when not cheaper. */
export function yearlySavingPct(monthlyMinor: Num, yearlyMinor: Num): number | null {
  const m = num(monthlyMinor);
  const y = num(yearlyMinor);
  if (m == null || y == null || m <= 0) return null;
  const pct = Math.round((1 - y / (m * 12)) * 100);
  return pct > 0 ? pct : null;
}

/** Where plans are bought for this audience; none for a member. */
export function plansHref(plan: PlanSummary | null): string | null {
  if (!plan) return null;
  if (plan.audience === "company_admin") return "/company/billing";
  if (plan.audience === "personal") return "/app/billing/plan";
  return null;
}

/**
 * The Next bill line under its date (DES 21-10-2026): the amount, and the
 * credit when credit pays some or all of it, e.g. "£68.97, £68.97 credit
 * applied". The credit figure is the server's (`nextBillCredit`); until
 * it sends one, a £0 bill from Stripe shows the plan's charge "paid from your
 * credit", never a made-up credit amount.
 * @param money Formats minor units in the plan's currency.
 */
export function nextBillNote(
  plan: PlanSummary,
  money: (minor: number) => string,
): { text: string; credit: boolean } | null {
  if (plan.cancelAtPeriodEnd) return null;
  const amount = plan.nextBillAmountMinor;
  const credit = plan.nextBillCreditMinor;
  if (amount != null && credit != null && credit > 0) {
    const gross = `${money(amount + credit)}, ${money(credit)} credit applied`;
    return { text: amount > 0 ? `${gross}, ${money(amount)} to pay` : gross, credit: true };
  }
  if (plan.nextBillExact && amount === 0) {
    return {
      text: plan.planChargeMinor ? `${money(plan.planChargeMinor)}, paid from your credit` : "Covered by your credit",
      credit: true,
    };
  }
  return amount != null ? { text: money(amount), credit: false } : null;
}
